/* Object page — the product detail page (PLAN.md phase 2). Shared by
   object.php ($isLibraryPhoto) and the own-photo variant ($isOwnPhoto) — hence
   the existence checks, because each page only holds part of the elements.
   No real pricing engine, no real cropper — those arrive in phase 6.
   Titles and images of the suspension systems come 1:1 from the database.

   Entry point for dist/js/object.js: whatever it imports ends up in that one
   bundle, so the page loads a single script. */

import './app.js';
import { createModal } from './modal.js';

(() => {
  /* Everything the server knows about this page: the suspension types with
     their completions, the made-to-measure warnings, and the labels. Rendered
     by object.php — see ExtraFuncs::getSuspensions(). */
  const DATA = window.OBJECT ?? {};
  const SUSPENSIONS = DATA.suspensions ?? [];
  const WARNINGS = DATA.warnings ?? {};
  const TEXT = DATA.text ?? {};

  /* The empty price as the server writes it: Funcs::getPrice() knows the
     shop's currency (VALUTA_*), so a pound shop shows £ and not €. It is
     markup (&pound;, &euro;), hence innerHTML wherever it lands. */
  const ZERO_PRICE = DATA.zeroPrice ?? '0';

  /* The size fields sit at the top because both the price calculation and the
     cropping (aspect ratio of the selection) need them. */
  const widthField = document.querySelector('[data-field="width"]');
  const heightField = document.querySelector('[data-field="height"]');

  /* Only on a shop with DIMENSIONS_IN_INCHES or DIMENSIONS_IN_FEET — empty
     everywhere else. Unit => field, `width-inches` under `inches`. They have
     no name: what is posted, priced and cropped is always the cm pair above. */
  const metricFields = (dimension) =>
    Object.fromEntries(
      [...document.querySelectorAll(`[data-field^="${dimension}-"]`)].map((field) => [
        field.dataset.field.slice(dimension.length + 1),
        field,
      ])
    );
  const widthMetrics = metricFields('width');
  const heightMetrics = metricFields('height');

  /* ── Logging ─────────────────────────────────────────────────────────
     Every action in the order form lands in the `log` table and shows up in
     the overview on beheer.dtpstudio.nl. The endpoint wants the action in
     `t` and accepts a fixed list of columns beside it — see the whitelist in
     site/services/index.php; anything outside it is dropped. The context
     (price, size, suspension, cart totals) is read out of the hidden fields
     on every call rather than remembered, so a line always carries the state
     at the moment of the action. Same shape as the old Product.log().

     `navigator` is deliberately not sent. It used to be, but the user agent
     string made the log table grow far out of proportion to what it was
     worth, so it was switched off — the server dropped it from the whitelist
     and the table has no column for it. The line stays here, commented, so
     its absence reads as a decision instead of an omission. */
  const LOG = {
    goToOwnPhoto: T.logImageToolGoToOwnPhoto,
    goToLibraryPhoto: T.logImageToolGoToLibraryPhoto,
    dimensionsEdit: T.logImageToolDimensionsEdit,
    enlargePhoto: T.logImageToolEnlargePhoto,
    suspensionInfo: T.logImageToolSuspensionInfo,
    suspensionInfoSelect: T.logImageToolSuspensionInfoSelect,
    suspensionSelect: T.logImageToolSuspensionSelect,
    suspensionOptionSelect: T.logImageToolSuspensionOptionSelect,
    uploadOtherPhoto: T.logImageToolUploadOtherPhoto,
    filter: T.logImageToolFilter,
    order: T.logImageToolOrder,
    contact: T.logImageToolContact,
    contactClose: T.logImageToolContactClose,
    contactSend: T.logImageToolContactSend,
  };

  const fieldValue = (name) => document.querySelector(`[data-field="${name}"]`)?.value ?? '';

  const log = (type, extra = {}) => {
    if (!type) return;

    const body = new URLSearchParams({
      t: type,
      object_type: fieldValue('type'),
      object_id: fieldValue('id'),
      object_value: fieldValue('pricevalue'),
      object_width: widthField.value || 0,
      object_height: heightField.value || 0,
      object_qty: 1,
      suspension: fieldValue('suspensionvalue'),
      total_amount: fieldValue('totalamountvalue'),
      total_vat: fieldValue('totalvatvalue'),
      total_shipping: fieldValue('totalshippingvalue'),
      total_discount: fieldValue('totaldiscountvalue'),
      // navigator: navigator.userAgent,   // see above — off on purpose
      ...extra,
    });

    /* Fire and forget: a lost line must never cost the visitor anything.
       `keepalive` lets the request finish when the click navigates away,
       which is what happens on the order button. */
    fetch('/site/services/?action=log', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    }).catch(() => {
      /* nothing to do about it, and nothing to show for it */
    });
  };

  /* ── The image on the server ─────────────────────────────────────────
     Both variants of this page work on a copy under /own-photo/<uuid>/, not
     on the file the visitor chose or on the library photo itself. That is the
     old contract and it is not cosmetic: update_image.php and saveOwnPhoto
     both take a uuid and refuse anything else, and the cart and the print
     application read that same uuid out of the `image` field.

     So an own photo is uploaded, and a library photo is copied into the same
     place by posting its path to the same endpoint. Once that has happened
     `imageName` holds the uuid, the `image` field carries it instead of the
     path object.php put there, and the filters have something to work on. */
  const imageField = document.querySelector('[data-field="imagetext"]');
  const jsonField = document.querySelector('[name="json"]');
  const uploadZone = document.querySelector('[data-upload-zone]');
  const uploadImage = document.querySelector('[data-upload-image]');
  const uploadPreview = document.querySelector('[data-upload-preview]');
  const uploadErrors = document.querySelectorAll('[data-upload-error]');
  const cropToggle = document.querySelector('[data-crop-toggle]');
  const cropArea = document.querySelector('[data-crop-area]');
  const cropFrame = document.querySelector('[data-crop-frame]');
  const cropHint = document.querySelector('[data-crop-hint]');

  /* "Uitsnede maken" is ticked unless the visitor unticked it; a library
     photo has no such box and no frame either. */
  const showCropFrame = () => {
    cropFrame.hidden = !cropToggle.checked;
    cropHint.hidden = !cropToggle.checked;
  };

  /* The selection, in fractions (0-1) of the displayed image, so it survives
     a window resize. Read again when the crop is sent along. */
  let crop = { x: 0, y: 0, w: 1, h: 1 };
  let imageName = '';
  let uploading = false;

  /* Two places can carry this message: at the foot of the upload frame while
     there is no photo, and above the preview once there is one. Only one of
     the two is ever on screen, so both are written and the hidden one costs
     nothing. The frame itself goes into the error state with it, the same way
     the two steps in the order form do. */
  const showUploadError = (message) => {
    uploadErrors.forEach((slot) => {
      slot.querySelector('[data-upload-error-text]').textContent = message ?? '';
      slot.hidden = !message;
    });

    uploadZone?.toggleAttribute('data-invalid', Boolean(message));
  };

  /* What the page shows for the image, so a filter can be seen. */
  const shownImage = uploadImage ?? document.querySelector('[data-object-image]');
  const libraryImageSource = uploadImage ? '' : (shownImage?.getAttribute('src') ?? '');

  const reloadImage = (filtered) => {
    if (!shownImage || !imageName) return;

    /* On a library page, taking every filter back off puts the sharp cms
       photo back: the copy under /own-photo/ is capped at OWN_PHOTO_MAX_WIDTH
       and re-encoded, so it is a step down for nothing. The own-photo page has
       no such original to fall back to — the template renders that <img> with
       an empty src — and there the working copy is simply what there is. */
    if (!filtered && libraryImageSource) {
      shownImage.src = libraryImageSource;

      return;
    }

    /* Always thumb_dummy: update_image.php rewrites it from the untouched
       original every time, so it holds the current state whether that is
       filtered or not. The query string is a cache-buster — the file keeps
       its name, only its content changed. */
    shownImage.src = `/own-photo/${imageName}/thumb_dummy.jpg?t=${performance.now().toFixed(0)}`;
  };

  /* Straight out of afterUpload() in the old app.js. */
  const afterUpload = (data) => {
    imageName = data.image;

    if (imageField) imageField.value = imageName;

    showUploadError('');
    updatePrice();
  };

  /* ── The working copy, made only when it is needed ───────────────────
     The old page copied a library photo into /own-photo/<uuid>/ on every
     single page view: six files, a couple of megabytes, for a visitor who may
     well have done nothing but look. A cron job swept them up afterwards.

     Nothing needs that copy until something is actually done to the image — a
     filter, or the order itself — so it is made at that moment instead. Whoever
     only looks leaves nothing behind. The price is a short wait on the first
     filter click and on the first order, where the copy now sits.

     `pending` is what keeps two quick clicks from starting two copies: the
     second waits on the same promise. An own photo does not come through here
     at all — its upload already produced the uuid. */
  let pending = null;

  const ensureImage = () => {
    if (imageName) return Promise.resolve(imageName);
    if (pending) return pending;
    if (DATA.isOwnPhoto || !imageField?.value) return Promise.resolve('');

    pending = fetch('/site/services/upload_image.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ libraryPhoto: imageField.value }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.status !== 'ok' || !data.image) return '';

        afterUpload(data);

        return imageName;
      })
      .catch(() => '')
      .finally(() => {
        pending = null;
      });

    return pending;
  };

  /* ── The crop, in the shape the server reads ─────────────────────────
     site/services/index.php (?action=saveOwnPhoto) was written against
     cropper.js and still expects its three objects. It works out the cut like
     this: the ratio between `imageData.naturalWidth` and `imageData.width` is
     how much larger the file is than what is on screen, and `cropboxData` is
     multiplied by that. `container` only matters when the image is narrower
     than the box around it — then it corrects for the centring.

     Our frame sits directly on the image (the figure is `w-fit`), so
     container and image are the same size and that correction comes out at
     zero. Which is exactly why this can be handed over without cropper.js. */
  const cropJson = () => {
    if (!uploadImage?.naturalWidth) return '';

    const box = uploadImage.getBoundingClientRect();

    return JSON.stringify({
      imageData: {
        naturalWidth: uploadImage.naturalWidth,
        naturalHeight: uploadImage.naturalHeight,
        width: box.width,
        height: box.height,
      },
      cropboxData: {
        left: crop.x * box.width,
        top: crop.y * box.height,
        width: crop.w * box.width,
        height: crop.h * box.height,
      },
      container: { width: box.width, height: box.height },
    });
  };

  const filterBox = (name) => document.querySelector(`[data-order-form] [name="${name}"]`);

  /* Black and white through GD, mirroring through Imagick — see
     site/services/tuinposter.php. The options go over as strings because the
     endpoint compares them to 'true'; a real boolean would arrive as "1" and
     silently never match. */
  const imageOptions = () => ({
    mirrorVertical: 'false',
    mirrorHorizontal: String(Boolean(filterBox('mirror')?.checked)),
    filter: filterBox('blackwhite')?.checked ? 'blackwhite' : '',
    cropped: cropToggle?.checked ? 1 : 0,
    json: jsonField?.value ?? '',
  });

  const anyFilter = () => Boolean(filterBox('mirror')?.checked || filterBox('blackwhite')?.checked);

  /* Rewrites the working copies under /own-photo/<uuid>/. `allImages` adds the
     print-resolution one, which the server then crops by calling itself — that
     one is deliberately not waited for, it takes seconds and nothing on screen
     depends on it. */
  let imageQueue = Promise.resolve();

  const updateImage = ({ allImages = false } = {}) => {
    const send = (type) => {
      const body = new URLSearchParams({ image: imageName });

      Object.entries(imageOptions()).forEach(([key, value]) =>
        body.append(`options[${key}]`, value)
      );

      return fetch(`/site/services/update_image.php?type=${type}`, {
        method: 'POST',
        credentials: 'same-origin',
        /* The print-resolution pass is sent and not waited for, and the very
           next thing that happens is the form leaving this page — which would
           cancel the request in flight. `keepalive` is what carries it over.
           Found the hard way: the cart was right and wc.jpg had never been
           cut. The old page had the same race and simply got away with it more
           often, because its synchronous ajax held the page still a moment
           longer. */
        keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
    };

    /* One at a time. Every call rewrites files under /own-photo/<uuid>/, and a
       filter switched a moment before the order is placed would otherwise have
       two of them writing the same jpeg. They queue.

       The print-resolution pass goes last and is not waited for: it takes
       seconds and nothing on screen depends on it. Last rather than first, so
       it starts once the thumbnail pass has finished with the folder. */
    imageQueue = imageQueue
      .catch(() => {})
      .then(async () => {
        if (!imageName) return;

        try {
          await send('thumb');
          reloadImage(anyFilter());
        } catch {
          /* The photo on screen stays what it was; saying nothing beats
             showing a filter that was never applied. */
        }

        if (allImages) send('original').catch(() => {});
      });

    return imageQueue;
  };

  /* The actual cut of the thumbnail, so what the visitor sees is what gets
     printed. The print-resolution one is done by update_image.php itself. */
  const saveOwnPhoto = async () => {
    if (!cropToggle?.checked || !imageName) return true;

    try {
      const response = await fetch('/site/services/?action=saveOwnPhoto&type=thumb', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ image: imageName, json: jsonField?.value ?? '' }),
      });
      const data = await response.json();

      return Boolean(data?.passed);
    } catch {
      return false;
    }
  };

  /* ── Own photo: upload + preview (eigen-foto.html only) ──────────────── */
  if (uploadZone) {
    const uploadInput = document.querySelector('[data-upload-input]');
    const uploadReset = document.querySelector('[data-upload-reset]');
    const progress = document.querySelector('[data-upload-progress]');
    const progressBar = document.querySelector('[data-upload-progress-bar]');

    /* The click on the hidden input bubbles back up to the zone; without this
       check that would immediately reopen the picker. */
    uploadZone.addEventListener('click', (e) => {
      if (e.target === uploadInput) return;
      uploadInput.click();
    });

    /* ── Dragging: the whole document is the drop zone ──────────────────
       This page has only one spot that expects an image, so wherever you let
       it go, that is where it belongs. It also stops the browser from simply
       opening a photo that lands beside the frame and navigating away from
       the page — filled-in sizes and all. Works once a photo is in place too:
       the new one replaces it.

       We listen on the document rather than the upload frame, because that
       frame disappears as soon as a photo is chosen. `dragenter`/`dragleave`
       fire for every element the pointer passes over; a counter tells us
       whether the drag really left the window. */
    const dropOverlay = document.querySelector('[data-drop-overlay]');
    let dragDepth = 0;

    /* Files only — not selected text or a dragged link. */
    const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

    const showDropOverlay = (on) => {
      if (!on) dragDepth = 0;
      dropOverlay.hidden = !on;
    };

    document.addEventListener('dragenter', (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      dragDepth += 1;
      showDropOverlay(true);
    });

    /* Without a preventDefault on dragover the browser refuses the drop. */
    document.addEventListener('dragover', (e) => {
      if (isFileDrag(e)) e.preventDefault();
    });

    document.addEventListener('dragleave', (e) => {
      if (!isFileDrag(e)) return;
      dragDepth -= 1;
      if (dragDepth <= 0) showDropOverlay(false);
    });

    document.addEventListener('drop', (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      showDropOverlay(false);

      /* Drag a pdf or a folder here and nothing happens — the frame stays put
         with its own note about JPG and PNG. */
      const file = e.dataTransfer.files?.[0];
      if (file?.type.startsWith('image/')) takePhoto(file);
    });

    uploadInput.addEventListener('change', () => {
      if (uploadInput.files?.[0]) {
        takePhoto(uploadInput.files[0]);
      }
    });

    /* ── Choosing a photo ─────────────────────────────────────────────────
       Two things happen at once. The file itself goes straight on screen out
       of the browser's own memory, so a visitor on a phone sees their photo
       the moment they pick it instead of watching a bar for eight megabytes.
       Meanwhile it is uploaded, and when that lands the preview is swapped
       for the thumbnail the server made.

       That swap is not cosmetic. The crop is sent as coordinates in the
       *displayed* image, and the server converts them with the ratio between
       its natural size and the size on screen. As long as the local file is
       showing, `naturalWidth` is the size of the original — a completely
       different number from the thumbnail the server will cut. So the frame
       stays out of reach until the thumbnail is in place. */
    const takePhoto = (file) => {
      if (!file.type.startsWith('image/')) {
        showUploadError(TEXT.wrongFileType);

        return;
      }

      uploadZone.hidden = true;
      uploadPreview.hidden = false;
      showUploadError('');

      const local = URL.createObjectURL(file);

      uploadImage.src = local;
      upload(file, local);
    };

    const setProgress = (fraction) => {
      if (!progress) return;

      progress.hidden = fraction === null;
      if (fraction !== null) progressBar.style.width = `${Math.round(fraction * 100)}%`;
    };

    /* XMLHttpRequest rather than fetch, for the one thing fetch cannot do:
       report how far an upload has got. */
    const upload = (file, localUrl) => {
      const body = new FormData();
      const request = new XMLHttpRequest();

      body.append('file', file);

      uploading = true;
      setProgress(0);

      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) setProgress(event.loaded / event.total);
      });

      request.addEventListener('load', () => {
        uploading = false;
        setProgress(null);

        let data = null;

        try {
          data = JSON.parse(request.responseText);
        } catch {
          /* html instead of json means the endpoint fell over */
        }

        if (data?.status !== 'ok' || !data.image) {
          URL.revokeObjectURL(localUrl);
          showUploadError(
            {
              'invalid-file-type': TEXT.wrongFileType,
              'photo-too-large': TEXT.photoTooLarge,
            }[data?.message] ?? TEXT.uploadFailed
          );
          uploadZone.hidden = false;
          uploadPreview.hidden = true;

          return;
        }

        afterUpload(data);

        /* Now the thumbnail, and only once it is really there is the crop
           frame worth anything — hence the work inside the load handler.

           `once` matters: a filter reloads this same <img> with a fresh
           thumb_dummy every time it is ticked, and a handler that stayed put
           would throw the visitor's own selection away on every tick. The
           thumbnail keeps its dimensions through a filter, so the selection
           stays valid as it is. */
        uploadImage.addEventListener(
          'load',
          () => {
            URL.revokeObjectURL(localUrl);

            /* The frame is laid out here and not the moment the upload
               returns: resetCrop() measures the picture, so it has to wait
               until the thumbnail is actually on the page. */
            resetCrop();
            showCropFrame();
          },
          { once: true }
        );
        uploadImage.src = `/own-photo/${data.image}/thumb.jpg`;

        /* "Uitsnede maken" starts ticked, so the frame comes up with the
           picture instead of waiting for a tick. A visitor who unticked it
           and then picks another photo keeps that choice. No help window with
           it any more: it opened over the photo the moment the upload
           finished, in front of a visitor who had not asked anything yet. The
           button that opens it is right there under the price for whoever
           does. */
        showCropFrame();
      });

      request.addEventListener('error', () => {
        uploading = false;
        setProgress(null);
        URL.revokeObjectURL(localUrl);
        showUploadError(TEXT.uploadFailed);
        uploadZone.hidden = false;
        uploadPreview.hidden = true;
      });

      request.open('POST', '/site/services/upload_image.php');
      request.withCredentials = true;
      request.send(body);
    };

    /* "Choose another photo" opens the file picker right away; the current
       photo stays until a new one is actually chosen (so cancelling leaves
       everything intact). Clearing `value` makes picking the same photo again
       fire a change event too. */
    uploadReset.addEventListener('click', () => {
      /* Logged on the press, not on the photo that follows: wanting a
         different photo is the signal, and cancelling the picker is part of
         that same doubt. */
      log(LOG.uploadOtherPhoto);
      uploadInput.value = '';
      uploadInput.click();
    });

    /* ── Cropping ──────────────────────────────────────────────────────
       Without a size filled in the selection is free and starts on the whole
       photo; with a size it follows that ratio. The selection is stored in
       fractions (0-1) of the displayed image, so it does not depend on the
       screen width. Dragging and pulling corners runs on pointer events (so
       touch works too). `crop` itself lives at the top of this file, because
       the selection has to be readable when it is sent along. The cutting
       happens server-side, in ObjectFuncs::saveOwnPhoto(). */
    const cropRatio = () => {
      const w = parseFloat(widthField.value);
      const h = parseFloat(heightField.value);

      return w > 0 && h > 0 ? w / h : null;
    };

    /* ── A portrait photo on a phone ────────────────────────────────────
       At the full width of the column such a photo comes out several times
       the height of the screen, and with the selection lying over it there
       was nothing left to put a finger on: the page would not scroll past
       its own preview. So the preview is held to part of the viewport
       height, which leaves the rest of the photo — and the page under it —
       within reach. A landscape photo never reaches the cap and stays
       exactly as wide as it was.

       The cap is a max-width on the figure rather than a max-height on the
       image, because the crop travels as fractions of the <img> box and
       that only holds while that box *is* the photo. A max-height would
       need object-contain, the photo would sit letterboxed inside a taller
       box, and the cut would land somewhere other than what was on screen.
       Through the width the photo keeps its ratio and the two rectangles
       stay the same one.

       The selection itself is in fractions, so it survives the resize
       without redrawing. */
    const PREVIEW_MAX_HEIGHT = 0.7;

    const fitPreview = () => {
      const { naturalWidth, naturalHeight } = uploadImage;

      if (!naturalWidth || !naturalHeight) return;

      const cap = (window.innerHeight * PREVIEW_MAX_HEIGHT * naturalWidth) / naturalHeight;

      cropArea.style.maxWidth = `${Math.round(cap)}px`;
    };

    /* Registered here and not once per upload: this runs for the local
       preview as well as for every thumbnail the server sends back, and it
       has to be in place before the handler that lays out the selection. */
    uploadImage.addEventListener('load', fitPreview);
    window.addEventListener('resize', fitPreview);

    const drawCrop = () => {
      cropFrame.style.left = `${crop.x * 100}%`;
      cropFrame.style.top = `${crop.y * 100}%`;
      cropFrame.style.width = `${crop.w * 100}%`;
      cropFrame.style.height = `${crop.h * 100}%`;
    };

    /* Largest rectangle with the requested ratio that fits inside the image,
       centred. Without a ratio: the whole image.

       Measured on the <img> and not on the <figure> around it, the same way
       cropJson() does. The figure carries a one pixel border, so its box is
       two pixels wider and taller — small, but it would mean the selection on
       screen and the selection that gets sent are answering to two different
       rectangles. */
    const resetCrop = () => {
      const ratio = cropRatio();
      const area = uploadImage.getBoundingClientRect();

      if (!ratio || !area.width || !area.height) {
        crop = { x: 0, y: 0, w: 1, h: 1 };
      } else {
        const imageRatio = area.width / area.height;
        const w = ratio >= imageRatio ? 1 : ratio / imageRatio;
        const h = ratio >= imageRatio ? imageRatio / ratio : 1;
        crop = { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
      }

      drawCrop();

      /* The size in the unit the visitor is typing in; the label carries
         one %s for it, like the other X-labels in webcopy. */
      const metric = metricValue?.value;
      const size = widthMetrics[metric]
        ? `${widthMetrics[metric].value} × ${heightMetrics[metric].value} ${{ inches: 'in', feet: 'ft' }[metric]}`
        : `${widthField.value} × ${heightField.value} cm`;

      cropHint.hidden = false;
      cropHint.querySelector('[data-crop-hint-text]').textContent = ratio
        ? (TEXT.cropFixed ?? '').replace('%s', size)
        : TEXT.cropFree ?? '';
    };

    const MIN = 0.05;
    const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

    cropFrame.addEventListener('pointerdown', (e) => {
      const corner = e.target.closest('[data-crop-corner]')?.dataset.cropCorner;
      const area = uploadImage.getBoundingClientRect();
      const start = { ...crop };
      const px = e.clientX;
      const py = e.clientY;
      const ratio = cropRatio();

      e.preventDefault();
      /* Capture is an optimisation (dragging outside the frame keeps working);
         if the browser refuses it, dragging must not break. */
      try {
        cropFrame.setPointerCapture(e.pointerId);
      } catch {
        /* no harm done */
      }

      const move = (ev) => {
        const dx = (ev.clientX - px) / area.width;
        const dy = (ev.clientY - py) / area.height;

        if (!corner) {
          crop.x = clamp(start.x + dx, 0, 1 - start.w);
          crop.y = clamp(start.y + dy, 0, 1 - start.h);
          drawCrop();

          return;
        }

        const west = corner.includes('w');
        const north = corner.includes('n');
        /* Width follows the horizontal drag; with a fixed ratio the height is
           derived from it, so the shape stays right. */
        let w = clamp(
          west ? start.w - dx : start.w + dx,
          MIN,
          west ? start.x + start.w : 1 - start.x
        );
        let h = ratio
          ? (w * (area.width / area.height)) / ratio
          : clamp(
              north ? start.h - dy : start.h + dy,
              MIN,
              north ? start.y + start.h : 1 - start.y
            );

        if (ratio && h > (north ? start.y + start.h : 1 - start.y)) {
          h = north ? start.y + start.h : 1 - start.y;
          w = (h * ratio) / (area.width / area.height);
        }

        crop = {
          w,
          h,
          x: west ? start.x + start.w - w : start.x,
          y: north ? start.y + start.h - h : start.y,
        };
        drawCrop();
      };

      const stop = () => {
        cropFrame.removeEventListener('pointermove', move);
        cropFrame.removeEventListener('pointerup', stop);
        cropFrame.removeEventListener('pointercancel', stop);
      };

      cropFrame.addEventListener('pointermove', move);
      cropFrame.addEventListener('pointerup', stop);
      cropFrame.addEventListener('pointercancel', stop);
    });

    /* Unticking "Uitsnede maken" is the old site's popup-help-cropping: any
       size goes, and the studio fits the photo and mails a proof first. The
       box stays unticked when the window closes — it explains, it does not
       ask. Unticked, `crop` is not posted, so the cart drops the json and
       update_image.php leaves the print-resolution copy uncut. */
    const cropDialog = createModal(document.querySelector('[data-dialog-crop]'));

    document.querySelectorAll('[data-close-crop]').forEach((button) =>
      button.addEventListener('click', () => cropDialog?.close())
    );

    cropToggle.addEventListener('change', () => {
      log(LOG.filter, { custom: cropToggle.checked ? 'enable crop image' : 'disable crop image' });

      if (cropToggle.checked && imageName) resetCrop();
      if (!cropToggle.checked) cropDialog?.open();

      if (imageName) showCropFrame();
    });

    /* Size changed while the frame is open: apply the ratio again, otherwise
       the selection no longer matches the order. */
    [widthField, heightField].forEach((field) => {
      field.addEventListener('input', () => {
        if (cropToggle.checked) resetCrop();
      });
    });
  }

  /* ── Price ──
     Straight from GardenposterFuncs::getPrice(). That formula is 2,100 lines
     with campaign overrides on top (PLAN.md §2.4); rebuilding it here would
     buy a round trip and cost the certainty that the screen and the invoice
     say the same thing. */
  const priceText = document.querySelector('[data-price]');
  const priceHint = document.querySelector('[data-price-hint]');
  const priceBefore = document.querySelector('[data-price-before]');
  const priceDiscount = document.querySelector('[data-price-discount]');
  const priceField = document.querySelector('[data-field="pricevalue"]');

  let chosenSystem = null;
  let chosenCompletion = '';
  let priceTimer = null;
  let priceRequest = 0;

  /* A size on its own is enough for a price: without a chosen suspension the
     calculation falls back to the cheapest system, plain cloth, exactly as
     GardenposterFuncs did through the old getPrice(). So 100 × 70 shows a
     price straight away, upload or no upload. The fallback only reaches the
     query — the hidden `suspension` field stays empty, so nothing can be
     ordered on a system the visitor never picked. */
  /* The Ads discount beside the price: the price before it and the
     percentage, both straight from the price answer — Ads::resetPrice() only
     adds them for a visitor with a valid ad. Called without an answer, both
     go away. */
  const showDiscount = (data = {}) => {
    const discount = Boolean(data.price_before_formatted && data.discount_percentage);

    priceBefore.innerHTML = discount ? data.price_before_formatted : '';
    priceDiscount.textContent = discount ? data.discount_percentage : '';
    priceBefore.hidden = !discount;
    priceDiscount.hidden = !discount;
  };

  const updatePrice = async (logInfo = null) => {
    const width = parseInt(widthField.value, 10);
    const height = parseInt(heightField.value, 10);
    const complete = width > 0 && height > 0;

    priceHint.hidden = complete;

    if (!complete) {
      priceText.innerHTML = ZERO_PRICE;
      showDiscount();
      if (priceField) priceField.value = '0';

      return;
    }

    /* Every keystroke starts a request; only the answer to the last one may
       land, or a slow early reply would overwrite a fresh price. */
    const ticket = ++priceRequest;

    const query = new URLSearchParams({
      action: 'getPrice',
      id: DATA.id ?? 0,
      type: chosenSystem ? chosenSystem.value : T.suspensionType.alleen_doek,
      x: width,
      y: height,
      completion: chosenCompletion,
      own_photo: DATA.isOwnPhoto ? 1 : 0,
    });

    try {
      const response = await fetch(`/site/services/?${query}`, { credentials: 'same-origin' });

      if (!response.ok) throw new Error(response.status);

      const data = await response.json();

      if (ticket !== priceRequest) return;

      /* Funcs::getPrice() writes &euro; and the like, so it is markup — as
         text it would read literally. */
      priceText.innerHTML = data.price_formatted ?? ZERO_PRICE;
      showDiscount(data);
      if (priceField) priceField.value = data.price ?? '0';

      /* The log line rides along with the price and is written only once the
         answer is in — that way it carries the price the visitor actually
         saw, which is the whole reason the column exists. Same order as the
         old getPrice(): no price, no line. */
      if (logInfo) log(logInfo.t, logInfo.extra);
    } catch {
      if (ticket !== priceRequest) return;

      /* Say nothing rather than something wrong: an old price on screen is
         worse than none. */
      priceText.innerHTML = ZERO_PRICE;
      showDiscount();
      priceHint.hidden = false;
    }
  };

  const debouncedUpdatePrice = (logInfo) => {
    clearTimeout(priceTimer);
    priceTimer = setTimeout(() => updatePrice(logInfo), 250);
  };

  /* The old page fired on every keyup, so "150" wrote three price calls and
     three log lines. The debounce collapses that to one — fewer rows in the
     overview than before for the same visitor, but each one holds a size the
     visitor actually stopped at. */
  [widthField, heightField].forEach((field) =>
    field.addEventListener('input', () =>
      debouncedUpdatePrice({ t: LOG.dimensionsEdit, extra: { custom: field.name } })
    )
  );

  updatePrice();

  /* ── Validation ──────────────────────────────────────────────────────
     The real validation lives in PHP: every change to a field goes to the
     endpoint, which answers with what is wrong. This is the hook that answer
     drives — no rules of its own, it only paints what it is handed.

       window.OrderForm.setErrors({ width: 'Minimaal 20 cm breed.' })
       window.OrderForm.setError('suspension', 'Kies eerst een ophangsysteem.')
       window.OrderForm.clearError('width')
       window.OrderForm.clearErrors()
       window.OrderForm.hasErrors()      // true / false
       window.OrderForm.getErrors()      // { width: '...' }

     Keys: 'width', 'height' and 'suspension' mark the field itself plus the
     step it belongs to; 'dimensions' marks step 1 as a whole without pointing
     at one of the two fields — for a message that is about the combination,
     such as a surface area that is too large. `setErrors` replaces everything
     at once, which is what an endpoint answer usually is; `setError` and
     `clearError` adjust one key.

     A message is optional: without one the block still turns red, just
     without a line of text. Submit stays disabled as long as there is at
     least one error, and errors only disappear when the endpoint says so —
     typing does not clear them by itself, otherwise the mockup would be
     contradicting the server. */
  const orderForm = document.querySelector('[data-order-form]');

  /* Which block a key belongs to, and which element is the field itself. */
  /* `fields` and not one `field`: "de maten zijn te klein" is about the pair,
     so both boxes go red. Until 16-09-2026 that error carried no field at all
     and the sentence stood under two boxes that looked fine. */
  const ERROR_KEYS = {
    width: { step: 'dimensions', fields: [widthField, ...Object.values(widthMetrics)] },
    height: { step: 'dimensions', fields: [heightField, ...Object.values(heightMetrics)] },
    dimensions: {
      step: 'dimensions',
      fields: [widthField, heightField, ...Object.values(widthMetrics), ...Object.values(heightMetrics)],
    },
    suspension: { step: 'suspension', fields: [orderForm.querySelector('[data-open-suspension]')] },
  };

  const steps = {
    dimensions: orderForm.querySelector('[data-step="dimensions"]'),
    suspension: orderForm.querySelector('[data-step="suspension"]'),
  };

  const submitButton = orderForm.querySelector('[type="submit"]');
  const errors = new Map();

  const paintErrors = () => {
    Object.entries(steps).forEach(([name, step]) => {
      const own = [...errors].filter(([key]) => ERROR_KEYS[key].step === name);
      const message = own
        .map(([, text]) => text)
        .filter(Boolean)
        .join(' ');
      const slot = step.querySelector('[data-step-error]');

      step.toggleAttribute('data-invalid', own.length > 0);
      slot.querySelector('[data-step-error-text]').textContent = message;
      slot.hidden = !message;
    });

    /* A box can be named by more than one error — its own and the pair's — so
       the verdicts are gathered first and written once: whichever comes last
       must not clear what the other one just set. */
    const verdict = new Map();

    Object.entries(ERROR_KEYS).forEach(([key, { fields = [] }]) => {
      const invalid = errors.has(key);

      fields.forEach((field) => {
        if (!field) return;

        verdict.set(field, (verdict.get(field) || false) || invalid);
      });
    });

    verdict.forEach((invalid, field) => {
      field.toggleAttribute('data-invalid', invalid);
      field.setAttribute('aria-invalid', String(invalid));
    });

    submitButton.disabled = errors.size > 0;
  };

  const rememberError = (key, message) => {
    if (!ERROR_KEYS[key]) {
      console.warn(`OrderForm: unknown key "${key}"`);

      return;
    }

    errors.set(key, typeof message === 'string' ? message : '');
  };

  window.OrderForm = {
    setError(key, message) {
      rememberError(key, message);
      paintErrors();
    },
    clearError(key) {
      errors.delete(key);
      paintErrors();
    },
    setErrors(all = {}) {
      errors.clear();
      Object.entries(all).forEach(([key, message]) => rememberError(key, message));
      paintErrors();
    },
    clearErrors() {
      errors.clear();
      paintErrors();
    },
    hasErrors: () => errors.size > 0,
    getErrors: () => Object.fromEntries(errors),
  };

  /* ── The two filters ─────────────────────────────────────────────────
     The label carries its look entirely in css (`has-checked`), so this only
     has to do the work: rewrite the copy on the server and put the result
     back on screen. Without an image there is nothing to rewrite — which is
     the case while an upload is still running. */
  const filterError = document.querySelector('[data-filter-error]');

  const showFilterError = (message) => {
    if (!filterError) return;

    filterError.querySelector('[data-filter-error-text]').textContent = message ?? '';
    filterError.hidden = !message;
  };

  ['blackwhite', 'mirror'].forEach((name) => {
    const box = orderForm.querySelector(`[name="${name}"]`);

    box?.addEventListener('change', async () => {
      log(LOG.filter, { custom: `${name}: ${box.checked}` });
      showFilterError('');

      /* A library photo has no working copy yet the first time — this is where
         it gets made. The checkbox itself keeps posting either way, so the
         print shop learns of the filter even if the preview cannot show it.

         The message goes to the filter slot and not to showUploadError: the
         two upload slots live in the own-photo branch of object.php, so on a
         library page that call writes into nothing at all — a click that
         visibly does nothing. This slot sits under the checkboxes, which is
         where the visitor is looking anyway. */
      if (!(await ensureImage()) && !DATA.isOwnPhoto) {
        showFilterError(TEXT.uploadFailed);

        return;
      }

      updateImage();
    });
  });

  /* ── Repairing what was flagged ──────────────────────────────────────
     The rule used to be that only the server cleared an error, because only
     the server knew whether it was gone. That rule dies the moment the page
     starts flagging things itself on submit: the button is disabled while
     there is an error, so without this a visitor who fills in the size they
     were just asked for can never press it again — the only thing that would
     clear the error is the submit that is now impossible.

     So editing what was flagged takes the flag off, and the submit puts it
     straight back if it is still wrong. */
  [widthField, heightField].forEach((field) =>
    field.addEventListener('input', () => {
      ['width', 'height', 'dimensions'].forEach((key) => window.OrderForm.clearError(key));
    })
  );

  /* ── Centimetres, inches or feet ────────────────────────────────────
     The inch and feet fields write whole centimetres into the cm fields and
     fire their `input`, so price, crop, log and error handling run exactly as
     if the visitor had typed cm — same rounding as the old UK themes.
     Switching back and forth converts what is filled in; the cm value stays
     the truth. */
  const metricValue = document.querySelector('[data-metric-value]');

  if (metricValue) {
    const CM_PER = { inches: 2.54, feet: 30.48 };
    const conversion = document.querySelector('[data-metric-conversion]');
    const pairs = [
      ['width', widthField, widthMetrics],
      ['height', heightField, heightMetrics],
    ];

    const showConversion = () => {
      // not drawn on a shop that hides cm
      if (!conversion) return;

      const filled = pairs.every(([, cm]) => Number(cm.value) > 0);

      pairs.forEach(([name, cm]) => {
        conversion.querySelector(`[data-conversion="${name}"]`).textContent = cm.value;
      });
      conversion.hidden = !(metricValue.value !== 'cm' && filled);
    };

    pairs.forEach(([, cm, metrics]) => {
      Object.entries(metrics).forEach(([metric, field]) =>
        field.addEventListener('input', () => {
          const value = parseFloat(field.value);

          cm.value = value > 0 ? String(Math.round(value * CM_PER[metric])) : '';
          cm.dispatchEvent(new Event('input'));
        })
      );

      cm.addEventListener('input', showConversion);
    });

    // a page that opens in inches or feet with a size in the url
    showConversion();

    /* The pill stands beside both field names; whichever is pressed switches
       both fields. The unit in use is aria-pressed and does nothing. */
    const switches = [...document.querySelectorAll('[data-metric-switch]')];

    switches.forEach((button) =>
      button.addEventListener('click', () => {
        const metric = button.dataset.metricSwitch;

        if (metric === metricValue.value) return;

        metricValue.value = metric;

        switches.forEach((other) =>
          other.setAttribute('aria-pressed', String(other.dataset.metricSwitch === metric))
        );

        pairs.forEach(([, cm, metrics]) => {
          const value = Number(cm.value);
          const shown = metrics[metric] ?? cm;

          /* Leave what the visitor typed alone as long as it still rounds to
             the same centimetres — 60 in must not come back as 59.84. */
          if (shown !== cm) {
            const typed = parseFloat(shown.value);
            const stale = Math.round(typed * CM_PER[metric]) !== value;

            if (stale) shown.value = value > 0 ? String(Number((value / CM_PER[metric]).toFixed(2))) : '';
          }

          [cm, ...Object.values(metrics)].forEach((field) => {
            field.closest('[data-metric]').hidden = field !== shown;
          });
          cm.closest('.flex-1').querySelector('[data-metric-label]').htmlFor = shown.id;
        });

        showConversion();
      })
    );
  }

  /* ── Ordering ────────────────────────────────────────────────────────
     Everything the old validateForm() did, in the same order, but without the
     synchronous ajax it used to freeze the browser with. The form is only
     really submitted once the server has agreed with the size, the working
     copies have been rewritten and the crop has been cut — otherwise the
     print shop would get an order for a photo that was never cut.

     The checks in front of that are the ones the endpoint cannot make: it
     does not know whether a photo was uploaded, and black and white cannot be
     asked of three of the suspension types. */
  const NO_BLACK_WHITE = [
    T.suspensionType?.bamboe_frame,
    T.suspensionType?.tuinhouten_frame,
    T.suspensionType?.zwevend_doek_tuinhouten_lijst,
  ].filter(Boolean);

  let submitting = false;

  const checkOrder = async () => {
    const width = widthField.value.trim();
    const height = heightField.value.trim();
    const whole = /^\d+$/;

    window.OrderForm.clearErrors();
    showUploadError('');
    showFilterError('');

    /* Sizes first: without them there is nothing to ask the endpoint. */
    const errors = {};

    if (!whole.test(width) || Number(width) <= 0) errors.width = '';
    if (!whole.test(height) || Number(height) <= 0) errors.height = '';

    if (Object.keys(errors).length) {
      errors.dimensions = T.errorProductDetailDimensionsRequired;
      window.OrderForm.setErrors(errors);

      return false;
    }

    if (!chosenSystem) {
      window.OrderForm.setError('suspension', T.errorProductDetailSuspensionRequired);

      return false;
    }

    if (DATA.isOwnPhoto && !imageName) {
      showUploadError(uploading ? TEXT.uploadFailed : TEXT.noPhotoYet);

      return false;
    }

    if (filterBox('blackwhite')?.checked && NO_BLACK_WHITE.includes(chosenSystem.value)) {
      showFilterError(TEXT.noBlackWhite);

      return false;
    }

    /* The size rules themselves live in GardenposterFuncs::checkDimensions —
       per suspension type, per country, with exceptions on top. Never in
       javascript: the answer decides what may be ordered. */
    const query = new URLSearchParams({
      action: 'checkDimensions',
      type: chosenSystem.value,
      x: width,
      y: height,
      source: 'x',
    });

    let dimensions = null;

    try {
      const response = await fetch(`/site/services/?${query}`, { credentials: 'same-origin' });

      dimensions = await response.json();
    } catch {
      window.OrderForm.setError('dimensions', TEXT.uploadFailed);

      return false;
    }

    if (!dimensions?.passed) {
      window.OrderForm.setError('dimensions', dimensions?.message ?? '');

      return false;
    }

    /* The endpoint may hand the sizes back tidied up. */
    widthField.value = dimensions.x_original;
    heightField.value = dimensions.y_original;

    /* Last moment at which the working copy can still be made: from here on
       the `image` field has to carry the uuid, because that is what the cart
       stores and the print application reads. A library photo that never had
       a filter touched arrives here without one.

       A failure does not block the order. The field then keeps the path
       object.php put there, which is exactly where the old page ended up when
       its copy-on-load failed — and refusing an order over a stumbling
       endpoint is the worse of the two. */
    await ensureImage();

    if (jsonField) jsonField.value = cropJson();

    await updateImage({ allImages: true });

    if (!(await saveOwnPhoto())) {
      showUploadError(TEXT.croppingFailed);

      return false;
    }

    return true;
  };

  /* ── Where the order went wrong ───────────────────────────────────────
     A failed check paints the block it belongs to: a red band around the
     sizes, one around the suspension picker, a line under the upload. On a
     phone that band sits a screen or more above the button that was pressed,
     so all that happens on screen is the button turning grey — the reason it
     did is out of sight, and the press reads as the page doing nothing.

     So the first thing that is wrong is brought into view. First in document
     order, which in a form filled in from the top down is also the first
     thing to put right. `data-invalid` marks the block as well as the field
     inside it and the block comes first, which is the one to show: it carries
     the sentence saying what is wrong.

     A zero height means the element is not on this screen at all — the upload
     message exists twice, once over the empty frame and once under the
     photo, and only one of the two is ever in the layout. */
  const TROUBLE = '[data-upload-error]:not([hidden]), [data-invalid], [data-filter-error]:not([hidden])';

  /* What the top of the window is covered by. Below `desk` that is the
     wrapper holding the phone number, from `desk` up the menu bar under it
     (see _header.php), so both are asked and the lower edge wins — whichever
     of the two is not sticking has scrolled out of the way and counts for
     nothing. */
  const coveredTop = () =>
    ['[data-sticky-header]', 'header'].reduce((edge, selector) => {
      const box = document.querySelector(selector)?.getBoundingClientRect();

      return Math.max(edge, box ? box.bottom : 0);
    }, 0);

  /* Enter in a size field submits the order with the phone's keyboard still
     open. `innerHeight` does not count the keyboard, so a message hidden
     behind it passed for "in view" and the page stayed put — and when the
     keyboard then closed, the page shifted under whatever scroll had started.
     So the field lets go first, which closes the keyboard, and the check waits
     for the visible area to grow back before it measures anything. The
     timeout covers a keyboard that was not open, or a browser that fires no
     resize. */
  const keyboardClosed = () =>
    new Promise((resolve) => {
      const typing = document.activeElement;
      const viewport = window.visualViewport;

      if (!typing?.matches('input, textarea') || !viewport) {
        typing?.blur?.();
        resolve();

        return;
      }

      const done = () => {
        viewport.removeEventListener('resize', done);
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(done, 500);

      viewport.addEventListener('resize', done);
      typing.blur();
    });

  /* The bottom of what can actually be seen: above the keyboard, where there
     is one. */
  const visibleBottom = () =>
    window.visualViewport
      ? window.visualViewport.offsetTop + window.visualViewport.height
      : window.innerHeight;

  const showTrouble = async () => {
    await keyboardClosed();

    const spot = [...document.querySelectorAll(TROUBLE)]
      .find((element) => element.getBoundingClientRect().height > 0);

    if (!spot) return;

    const box = spot.getBoundingClientRect();

    /* Already there, and not tucked under the header: leave the page alone. */
    if (box.top >= coveredTop() && box.bottom <= visibleBottom()) return;

    /* No `behavior` here: left out, it follows the `scroll-behavior` of the
       page, which app.css sets to smooth and turns off again under
       prefers-reduced-motion. Naming it would take that choice away. */
    spot.scrollIntoView({ block: 'center' });
  };

  orderForm.addEventListener('submit', async (event) => {
    /* The second pass, the one this handler let through itself. */
    if (submitting) return;

    event.preventDefault();
    log(LOG.order);

    submitButton.disabled = true;

    const passed = await checkOrder();

    submitButton.disabled = window.OrderForm.hasErrors();

    if (!passed) {
      showTrouble();

      return;
    }

    submitting = true;
    orderForm.submit();
  });

  /* ── Suspension system: the picker ───────────────────────────────────
     In place of magnific-popup (PLAN.md §2.1), on the modal from modal.js
     rather than on <dialog> — see the comment at the top of that file for
     why. The detail window opens on top of the picker, so two are open at
     once and the stack in modal.js keeps them apart. */
  const suspensionDetailElement = document.querySelector('[data-dialog-suspension-detail]');
  const completionElement = document.querySelector('[data-dialog-completion]');
  const suspensionDialog = createModal(document.querySelector('[data-dialog-suspension]'));
  const suspensionDetailDialog = createModal(suspensionDetailElement);
  const completionDialog = createModal(completionElement);
  const suspensionList = document.querySelector('[data-suspension-list]');
  const suspensionText = document.querySelector('[data-suspension-text]');

  const chooseButton = document.querySelector('[data-detail-choose]');

  const detail = {
    title: document.querySelector('[data-detail-title]'),
    images: document.querySelector('[data-detail-images]'),
    description: document.querySelector('[data-detail-description]'),
    completionList: document.querySelector('[data-detail-completion-list]'),
  };

  /* The alt of a completion image is empty, but the title of a system comes
     out of the database — so it is escaped before it lands in an attribute. */
  const escapeHtml = (value) =>
    String(value ?? '').replace(
      /[&<>"]/g,
      (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[character]
    );

  /* Equal height per block: the card itself is `h-full flex flex-col` and the
     text line gets `flex-1` — a grid row stretches its columns to the same
     height anyway, this makes the title line up neatly within that height
     whether it runs to one line or two.

     Two separate actions on one card, so both get their own affordance:
     clicking the photo picks the system (which is why hovering shows "Kies
     dit systeem" over the photo), clicking the Info button opens the
     explanation.

     That Info button sits half over the photo and half over the white strip
     below it, the way the current site does it: on that edge it stands out,
     while a light green block in the middle of a busy photo would disappear.
     The label is white, at the client's request and against the measurement:
     white on this green is 1.7:1 where 4.5 is the floor, and the dark ink it
     replaced was 9.3:1. Nothing about the position changes that — the label
     sits on the green either way. Same combination as the accent button in
     the main navigation, so if this comes back it comes back in both. */
  suspensionList.innerHTML = SUSPENSIONS.map(
    (system) => `
    <li>
      <div class="h-full flex flex-col rounded-md border border-line bg-white overflow-hidden transition-colors hover:border-accent hover:bg-accent-soft">
        <div class="relative group">
          <button type="button" data-system="${system.value}" class="block w-full cursor-pointer">
            <img src="${system.image}" alt="" loading="lazy" class="w-full aspect-[4/3] ${system.fit || 'object-contain'} bg-white">
            <span class="absolute inset-0 grid place-items-center bg-ink/0 group-hover:bg-ink/40 transition-colors">
              <span class="px-3 py-1.5 rounded-md bg-white text-ink text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                ${escapeHtml(T.chooseThisSuspensionType || '')}
              </span>
            </span>
          </button>
          <button type="button" data-system-info="${system.value}"
                  class="absolute left-1/2 -translate-x-1/2 -bottom-3 z-10 inline-flex items-center h-7 px-3.5 rounded-md bg-accent hover:bg-accent-light text-white text-xs font-semibold shadow-sm cursor-pointer">
            ${escapeHtml(TEXT.info || '')}
          </button>
        </div>
        <button type="button" data-system="${system.value}" class="flex-1 px-2.5 pt-5 pb-2.5 text-center text-sm font-semibold text-ink hover:text-brand cursor-pointer">
          ${system.title}
        </button>
      </div>
    </li>
  `
  ).join('');

  const suspensionField = document.querySelector('[data-field="suspensionvalue"]');
  const completionField = document.querySelector('[name="completion"]');
  const dimensionWarning = document.querySelector('[data-dimension-warning]');

  /* Made to measure: with some systems the cloth ends up larger or smaller
     than what you type. Which warning belongs to it depends on the system and
     sometimes on the completion, so it is looked up under both keys — that
     table comes from ExtraFuncs::getDimensionWarnings(). */
  const showWarning = () => {
    if (!dimensionWarning) return;

    const key = chosenSystem
      ? [`${chosenSystem.value}-${chosenCompletion}`, String(chosenSystem.value)].find(
          (candidate) => WARNINGS[candidate]
        )
      : null;

    /* Html out of the webcopy, so it goes in as it is — the icon beside it
       lives in the template and must survive. */
    dimensionWarning.querySelector('[data-dimension-warning-text]').innerHTML = key
      ? WARNINGS[key]
      : '';
    dimensionWarning.hidden = !key;
  };

  /* `source` only decides which action ends up in the log: 'list' is a click
     on a card in the picker, 'detail' is the button inside the explanation.
     Those are two different visitors — one recognises the system, the other
     had to read first — and the overview has had a separate number for that
     distinction all along. */
  const chooseSystem = (system, completion = '', source = 'list') => {
    chosenSystem = system;
    chosenCompletion = completion;

    const option = (system.completions ?? []).find((item) => item.value === completion);

    /* The completion on a line of its own, and smaller (client, 27 Aug 2026).
       The two ran together behind an em dash — "Bamboe frame — Opgespannen met
       elastieken" — which wraps wherever the button happens to end and reads as
       one long name rather than as a choice and the way it is finished. Two
       lines say which is which without a dash having to. */
    suspensionText.innerHTML = option
      ? `${escapeHtml(system.title)}<span class="block text-sm">${escapeHtml(stripTags(option.text))}</span>`
      : escapeHtml(system.title);
    suspensionText.classList.remove('text-ink-soft');
    suspensionText.classList.add('font-semibold', 'text-brand');

    /* The form posts these two, not the label on the button. */
    if (suspensionField) suspensionField.value = system.value;
    if (completionField) completionField.value = completion;

    /* Picking one is the repair for "choose a suspension type first". */
    window.OrderForm.clearError('suspension');

    showWarning();

    /* A completion is its own action whatever it was picked from, the way the
       old page had it; without one the origin decides. `suspension` and the
       rest of the context come out of the hidden fields, just set above. */
    updatePrice(
      completion
        ? { t: LOG.suspensionOptionSelect, extra: { custom: completion } }
        : { t: source === 'detail' ? LOG.suspensionInfoSelect : LOG.suspensionSelect }
    );
  };

  /* The completion texts are html out of the webcopy; on a button we only
     want the words. */
  const stripTags = (value) => {
    const holder = document.createElement('div');

    holder.innerHTML = value ?? '';

    return (holder.textContent ?? '').trim().split('\n')[0];
  };

  const showDetail = (system) => {
    /* The photo of the system itself and nothing else. The completion photos
       (rope, elastics, standard and wide frame) belong to the completion
       window, where they sit on the card that picks them; stacked under the
       main photo here they read as part of the system (Steve, 15-10-2026). */
    const images = [{ src: system.imageLarge ?? system.image, alt: system.title }];

    detail.title.textContent = system.title;
    detail.images.innerHTML = images
      .map(
        (image) =>
          `<img src="${image.src}" alt="${escapeHtml(image.alt)}" class="block w-full h-auto">`
      )
      .join('');
    /* The description is html out of the webcopy, so it goes in as it is;
       .webcopy on the wrapper dresses it. */
    detail.description.innerHTML = system.description ?? '';

    suspensionDetailElement.dataset.active = system.value;
    suspensionDetailDialog.open();
  };

  /* The completion in a window of its own, over whatever it was opened from.
     It used to hang under the explanation, where a screen of text stood
     between the visitor and the only thing left to do. */
  const showCompletions = (system) => {
    /* A photograph, the size of the frame, and the button that picks it.

       There were three things here to begin with: a framed card, the
       completion's own text under the photo as a heading, and a button
       carrying COMPLETION_SELECT — one sentence written once and printed on
       every card, so "kies deze lijst" stood on both halves of the same
       choice and told you nothing about which half. The button says what it
       picks now, the heading above it went with the words moved onto it, and
       with the words gone there was nothing left for a border to hold
       together (client, 27 Aug 2026).

       ExtraFuncs::getSuspensions() splits that block in two, because php is
       where the html is: `title` is the heading, `subtitle` the line under it
       — "6,5 x 4 cm" against "9 x 4 cm" on the two wooden frames, which is
       the whole difference between them and so belongs on the card rather
       than only in the explanation window behind the Info button.
       COMPLETION_SELECT is still the fallback for a completion whose block
       has no heading to take, and a block that is a heading and nothing else
       — the two bamboo ones — has no second line to print.

       The measurements sit under the button rather than over it: above, they
       read as a caption to the photograph and push the two buttons apart on a
       card whose neighbour has no second line; underneath, every card carries
       its button at the same height and the size reads as what it is, a note
       on the frame just picked.

       `mt-auto` sits on the pair, so two photographs of different heights
       still leave the buttons on one line; a completion with no picture at
       all — the three eye spacings — is simply the button, which is what it
       always was. */
    detail.completionList.innerHTML = (system.completions ?? [])
      .map(
        (option) => `<li>
            <button type="button" data-pick-completion="${escapeHtml(option.value)}"
                    class="group w-full h-full flex flex-col gap-3 text-center cursor-pointer">
              ${option.image ? `<img src="${option.image}" alt="" loading="lazy" class="block w-full h-auto rounded-md">` : ''}
              <span class="mt-auto flex flex-col gap-2">
                <span class="btn btn-order btn-sm group-hover:bg-order-dark">
                  ${escapeHtml(option.title || TEXT.completionSelect || 'Kies')}
                </span>
                ${option.subtitle ? `<span class="text-sm text-ink-soft">${escapeHtml(option.subtitle)}</span>` : ''}
              </span>
            </button>
          </li>`
      )
      .join('');

    completionElement.dataset.active = system.value;
    completionDialog.open();
  };

  /* One road for every "choose this system", wherever it is pressed: without
     a completion it lands straight on the order form, with one it opens the
     small window first, and a system with a single option has nothing to ask.
     `source` only travels along for the log — a card in the picker and the
     button in the explanation are two different visitors. */
  const pickSystem = (system, source) => {
    if (!system) return;

    const completions = system.completions ?? [];

    if (completions.length > 1) {
      showCompletions(system);

      return;
    }

    chooseSystem(system, completions.length === 1 ? completions[0].value : '', source);
    suspensionDetailDialog.close();
    suspensionDialog.close();
  };

  document.querySelector('[data-open-suspension]').addEventListener('click', () => {
    suspensionDialog.open();
  });

  document.querySelector('[data-close-suspension]').addEventListener('click', () => {
    suspensionDialog.close();
  });

  suspensionList.addEventListener('click', (e) => {
    const infoButton = e.target.closest('[data-system-info]');

    if (infoButton) {
      const system = SUSPENSIONS.find((s) => s.value === Number(infoButton.dataset.systemInfo));

      if (system) {
        /* Straight to the log, not through the price: opening an explanation
           says something on its own, also from a visitor who has not filled
           in a size yet. */
        log(LOG.suspensionInfo, { suspension: system.value });
        showDetail(system);
      }

      return;
    }

    const button = e.target.closest('[data-system]');

    if (!button) return;

    /* A card picks the system; where a completion is compulsory it opens the
       small window instead, the way the old selectSuspension() opened its
       options popup. */
    pickSystem(
      SUSPENSIONS.find((s) => s.value === Number(button.dataset.system)),
      'list'
    );
  });

  document.querySelector('[data-close-suspension-detail]').addEventListener('click', () => {
    suspensionDetailDialog.close();
  });

  document.querySelector('[data-detail-back]').addEventListener('click', () => {
    suspensionDetailDialog.close();
  });

  document.querySelector('[data-close-completion]').addEventListener('click', () => {
    completionDialog.close();
  });

  /* Back closes only this window; whatever it opened over — the picker or the
     explanation — is still standing underneath. */
  document.querySelector('[data-completion-back]').addEventListener('click', () => {
    completionDialog.close();
  });

  const bySystemValue = (element) =>
    SUSPENSIONS.find((system) => system.value === Number(element.dataset.active));

  /* "Choose this system" out of the explanation goes down the same road as a
     card, so it too opens the small window when there is a completion left to
     settle. */
  chooseButton.addEventListener('click', () =>
    pickSystem(bySystemValue(suspensionDetailElement), 'detail')
  );

  /* Picking a completion picks the system with it, in one press, and closes
     everything that was open behind it. */
  detail.completionList?.addEventListener('click', (event) => {
    const option = event.target.closest('[data-pick-completion]');
    const system = bySystemValue(completionElement);

    if (!option || !system) return;

    chooseSystem(system, option.dataset.pickCompletion, 'detail');
    completionDialog.close();
    suspensionDetailDialog.close();
    suspensionDialog.close();
  });

  /* ── Enlarge photo (product.html only, library photo) ────────────────── */
  const lightboxDialog = createModal(document.querySelector('[data-dialog-lightbox]'));

  if (lightboxDialog) {
    document.querySelector('[data-open-lightbox]').addEventListener('click', (e) => {
      e.preventDefault();
      log(LOG.enlargePhoto);
      lightboxDialog.open();
    });

    document.querySelector('[data-close-lightbox]').addEventListener('click', () => {
      lightboxDialog.close();
    });
  }

  /* ── Need help / contact ─────────────────────────────────────────────
     Replaces #popup-contact (magnific-popup, object.php). The same window
     opens from the "Hulp nodig?" button (context "default") and automatically
     after a successful upload of an own photo (context "upload"). */
  const contactForm = document.querySelector('[data-contact-form]');
  const contactSuccess = document.querySelector('[data-contact-success]');
  const contactIntros = document.querySelectorAll('[data-contact-intro]');

  /* Closing without sending is the interesting half of this window, so it
     gets its own action. The flag is what tells the two apart: after a send
     the window stays open on the thank-you text, and closing that is not an
     abandonment. Applies to the window that opens by itself after an upload
     too — walking away from it says the same thing.

     `onRequestClose` catches all three ways out at once — the backdrop and
     escape run through modal.js, the cross calls it directly — so the line
     goes out wherever the visitor leaves. */
  let contactSent = false;

  const contactDialog = createModal(document.querySelector('[data-dialog-contact]'), {
    onRequestClose: () => {
      if (!contactSent) log(LOG.contactClose);

      contactDialog.close();
    },
  });

  const openContact = (context) => {
    contactSent = false;
    contactIntros.forEach((intro) => {
      intro.hidden = intro.dataset.contactIntro !== context;
    });
    contactForm.reset();
    contactForm.hidden = false;
    contactSuccess.hidden = true;
    contactDialog.open();
  };

  /* The log sits on the button and not in openContact(), which is where it
     belongs now that nothing else calls it: an upload used to open this window
     on its own, and that was never the visitor asking for help. */
  document.querySelectorAll('[data-open-contact]').forEach((button) => {
    button.addEventListener('click', () => {
      log(LOG.contact);
      openContact(button.dataset.openContact);
    });
  });

  document.querySelectorAll('[data-close-contact]').forEach((button) => {
    button.addEventListener('click', () => contactDialog.requestClose());
  });

  /* Really sent now. The endpoint mails the request and reserves the uploaded
     photo, and it reads its fields under exactly these names — which is why
     they were left alone in object.php. `type` is which of the two texts was
     showing, the way the old page read it off the visible intro. */
  contactForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    log(LOG.contactSend);

    const sendButton = contactForm.querySelector('[data-contact-send]');
    const body = new URLSearchParams(new FormData(contactForm));
    const context = [...contactIntros].find((intro) => !intro.hidden);

    body.append('id', fieldValue('id'));
    body.append('type', context?.dataset.contactIntro ?? 'default');

    sendButton.disabled = true;

    try {
      const response = await fetch('/site/services/?action=modalOwnPhoto', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const data = await response.json();

      if (!data?.success) throw new Error('rejected');

      contactSent = true;
      contactForm.hidden = true;
      contactSuccess.hidden = false;
    } catch {
      /* The endpoint answers with nothing but success true or false — it does
         not say which field it tripped over — so the form stays as it is and
         the visitor can try again. */
      sendButton.disabled = false;
    }
  });

  /* Opening the page is itself an action in the overview, and it is the line
     every later one on this page is measured against. Last, so a failure
     anywhere above cannot swallow it — and `object_id` tells the two library
     photos apart, since it travels in every payload. */
  log(DATA.isOwnPhoto ? LOG.goToOwnPhoto : LOG.goToLibraryPhoto);
})();
